/** @jsxImportSource @emotion/react */
import { keyframes } from '@emotion/react';
import {
  Button,
  createStyles,
  Icon,
  Input,
  Typography,
  useTheme,
} from '@quickbit/qb-design-system';
import { ChangeEvent, useCallback, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import { useAsyncFn } from 'react-use';
import { ErrorResponse, FormErrorResponse } from 'api/fetchAPI';
import { ErrorPage, Markdown } from 'components';
import { useOrderId } from 'context';
import { useOrderDetails, usePoll } from 'hooks';
import {
  completeExternalRefund,
  fetchOrderStatus,
  getTravelRuleInfoStatus,
  OrderStatusResponse,
  TravelRuleInfoStatus,
} from 'pages/api';
import { OrderStatus } from 'types';
import { redirectBasedOnStatus } from 'utils';
import { initiateRefund } from './api';

const contactSupport = () => {
  window.open('mailto:support@quickbit.com');
};

export const InvalidAmount = () => {
  const { palette, spacing } = useTheme();
  const { order, error: orderError } = useOrderDetails();
  const orderId = useOrderId();
  const navigate = useNavigate();
  const { formatMessage } = useIntl();
  const [address, setAddress] = useState('');
  const [addressError, setAddressError] = useState<string | null>(null);
  const [orderStatus, setOrderStatus] = useState<OrderStatus | null>(null);
  const [travelRuleWindow, setTravelRuleWindow] = useState<Window | null>(null);
  const styles = useStyles();

  const checkStatus = useCallback(
    ({ status }: OrderStatusResponse) => {
      if (status === 'REFUND_CRYPTO_RECEIVED' || status === 'INVALID_AMOUNT') {
        setOrderStatus(status);
        return null;
      }
      return redirectBasedOnStatus(status, navigate) || null;
    },
    [navigate]
  );

  const poll = useCallback(() => fetchOrderStatus(orderId), [orderId]);

  const {
    start,
    error: pollingError,
    isPolling,
  } = usePoll({
    onPoll: checkStatus,
    poll,
  });

  useEffect(() => {
    if (!isPolling) start();
  }, [isPolling, start]);

  const handleChange = useCallback(
    (e: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
      setAddress(e.target.value);
      setAddressError(null);
    },
    [setAddress]
  );

  const handlePaste = useCallback(async () => {
    const pastedText = await navigator.clipboard.readText();
    setAddress(pastedText);
    setAddressError(null);
  }, [setAddress]);

  const openTravelRuleForm = (previewId: string) => {
    const openedWindow = window.open(
      `${process.env.REACT_APP_WEB_APP_BASE_URL}/travel-rule-beneficiary-info/${previewId}?isPayRefund=true`
    );
    setTravelRuleWindow(openedWindow);
  };

  const [
    { error: completeRefundError, loading: completeRefundLoading },
    completeSend,
  ] = useAsyncFn(
    async (previewId: string) => {
      await completeExternalRefund(orderId, previewId, true);
      navigate('/refunding');
    },
    [orderId]
  );

  const [
    {
      value: initiateRefundValue,
      loading: initiateRefundLoading,
      error: initiateRefundError,
    },
    refund,
  ] = useAsyncFn(async () => {
    if (!order) return;
    const response = await initiateRefund(order.order.uuid, address);
    if (!response.isInternal) {
      startTravelRulePolling();
      openTravelRuleForm(response.sendPreviewId);
    } else {
      completeSend(response.sendPreviewId);
    }
    return response.sendPreviewId;
  }, [order, address, initiateRefund]);

  const getTravelRuleStatus = useCallback(async () => {
    if (initiateRefundValue)
      return getTravelRuleInfoStatus(initiateRefundValue);
    return Promise.resolve({ status: false });
  }, [initiateRefundValue]);

  const checkTravelRuleSuccess = useCallback(
    (data: TravelRuleInfoStatus) => {
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      if (data && data.status) {
        if (travelRuleWindow) {
          travelRuleWindow.close();
          setTravelRuleWindow(null);
        }
        if (initiateRefundValue) completeSend(initiateRefundValue);
        return true;
      }
      return null;
    },
    [travelRuleWindow, completeSend, initiateRefundValue]
  );

  const {
    start: startTravelRulePolling,
    error: travelRuleInfoError,
    isPolling: isPollingTravelRule,
  } = usePoll({
    poll: getTravelRuleStatus,
    onPoll: checkTravelRuleSuccess,
  });

  const receivedAmount = order?.order.refund?.receivedAmount;

  const isFormErrorResponse = (
    error: ErrorResponse
  ): error is FormErrorResponse => {
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    return (error as FormErrorResponse).formErrors !== undefined;
  };

  const error =
    initiateRefundError ??
    orderError ??
    pollingError ??
    travelRuleInfoError ??
    completeRefundError;

  useEffect(() => {
    if (initiateRefundError && isFormErrorResponse(initiateRefundError)) {
      setAddressError(
        initiateRefundError.formErrors.map((err) => err.reason).join('; ')
      );
    }
  }, [initiateRefundError]);

  if (error && !isFormErrorResponse(error)) {
    return <ErrorPage error={error} tryAgainAction={() => navigate(0)} />;
  }

  return (
    <>
      <div css={{ marginBottom: spacing.xxxs }}>
        <Typography variant="subtitle">
          <FormattedMessage id="page.invalidAmount.heading" />
        </Typography>
      </div>

      {orderStatus === null && (
        <>
          <div css={styles.loaderContainer}>
            <div css={styles.loaderWrapper}>
              <span css={styles.loader} />
            </div>
          </div>
          <Typography variant="bodySmall" color={palette.grey.default}>
            <FormattedMessage
              id="page.invalidAmount.gatherOrderData"
              values={{ amount: receivedAmount }}
            />
          </Typography>
        </>
      )}

      {orderStatus !== null && orderStatus !== 'REFUND_CRYPTO_RECEIVED' && (
        <Typography variant="bodySmall" color={palette.grey.default}>
          <FormattedMessage
            id="page.invalidAmount.waitForCrypto"
            values={{ amount: receivedAmount }}
          />
        </Typography>
      )}
      {orderStatus == 'REFUND_CRYPTO_RECEIVED' && (
        <>
          <Typography variant="bodySmall" color={palette.grey.default}>
            <FormattedMessage
              id="page.invalidAmount.subHeading"
              values={{ amount: receivedAmount }}
            />
          </Typography>
          <div css={{ marginTop: spacing.xs, marginBottom: spacing.xxxs }}>
            <Input
              value={address}
              onChange={handleChange}
              placeholder={formatMessage({
                id: 'page.invalidAmount.refundAddress',
              })}
              endAdornment={
                <div css={{ cursor: 'pointer' }} onClick={handlePaste}>
                  <Icon name="paste" />
                </div>
              }
            />
            {addressError && (
              <Typography
                variant="captionMedium"
                color={palette.danger.default}
              >
                {addressError}
              </Typography>
            )}
          </div>
          <Typography variant="captionSmallMedium" color={palette.grey.default}>
            <FormattedMessage id="page.invalidAmount.caption" />
          </Typography>
          {isPollingTravelRule && initiateRefundValue && (
            <Markdown
              markdown={`To comply with regulations, you need to provide information about the recipient. [Click here](${process.env.REACT_APP_WEB_APP_BASE_URL}/travel-rule-beneficiary-info/${initiateRefundValue}?isPayRefund=true) to go to the form.`}
              styles={{
                color: palette.grey.default,
                fontSize: '0.85rem',
              }}
            />
          )}
          <div css={{ marginTop: spacing.xs, marginBottom: spacing.xxs }}>
            <Button
              loading={
                initiateRefundLoading ||
                completeRefundLoading ||
                isPollingTravelRule
              }
              text={formatMessage({ id: 'page.invalidAmount.submit' })}
              onClick={refund}
              disabled={
                address.length === 0 ||
                initiateRefundLoading ||
                isPollingTravelRule ||
                completeRefundLoading
              }
            />
          </div>
          <Button
            variant="secondary"
            text={formatMessage({ id: 'page.invalidAmount.contactSupport' })}
            onClick={contactSupport}
          />
        </>
      )}
    </>
  );
};

const useStyles = createStyles(({ palette, spacing, shape }) => ({
  loaderContainer: {
    margin: `${spacing.xxxs} 0`,
    width: '100%',
  },
  loaderWrapper: {
    width: '100%',
    background: palette.primary.x50,
    height: `calc(${spacing.xxxs} / 2)`,
    position: 'relative',
    overflow: 'hidden',
  },
  loader: {
    height: '100%',
    position: 'absolute',
    borderRadius: shape.borderRadius,
    animation: `3s cubic-bezier(0.65, 0.815, 0.735, 0.395) 0s infinite normal none running ${keyframes`
                0% {
                  left: -35%;
                  right: 100%;
                }
                60% {
                    left: 100%;
                    right: -90%;
                }
                100% {
                    left: 100%;
                    right: -90%;
                }
              `}`,
    background: palette.primary.default,
  },
}));
